/*
 * <drag-zoom-wrapper>
 *
 * Implements the interactive zoom that was left unimplemented in 7.1.0:
 *
 *   - mouse wheel       → zoom in/out, anchored at the cursor
 *   - double click/tap  → toggle 1x ↔ 2.5x at the click point
 *   - mouse drag        → pan (only when zoomed in)
 *   - touch pinch       → zoom in/out with two fingers
 *   - touch drag        → pan (only when zoomed in)
 *
 * The element drives three CSS custom properties on its inner <img>:
 *   --drag-zoom-scale, --drag-zoom-translate-x, --drag-zoom-translate-y
 *
 * Those vars are consumed in component-drag-zoom.css with a transform
 * order of translate() scale() so the JS pans in plain screen pixels.
 *
 * State is reset when the parent <zoom-dialog> closes (listens to
 * the 'dialog-close' window event already dispatched by zoom-dialog.js).
 *
 * Optional override: <drag-zoom-wrapper data-max-scale="6">.
 */

if (!customElements.get('drag-zoom-wrapper')) {
  class DragZoomWrapper extends HTMLElement {
    constructor() {
      super();
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.minScale = 1;
      this.maxScale = parseFloat(this.dataset.maxScale) || 4;
      this.zoomStep = 0.3;

      this._dragging = false;
      this._dragStart = null;
      this._translateStart = null;
      this._pointers = new Map();
      this._pinchStartDistance = null;
      this._pinchStartScale = 1;

      this._onWheel = this._onWheel.bind(this);
      this._onDblClick = this._onDblClick.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onContextMenu = this._onContextMenu.bind(this);
      this._reset = this._reset.bind(this);
    }

    get image() {
      return this.querySelector('img.product-media__image') || this.querySelector('img');
    }

    connectedCallback() {
      this.addEventListener('wheel', this._onWheel, { passive: false });
      this.addEventListener('dblclick', this._onDblClick);
      this.addEventListener('pointerdown', this._onPointerDown);
      this.addEventListener('pointermove', this._onPointerMove);
      this.addEventListener('pointerup', this._onPointerUp);
      this.addEventListener('pointercancel', this._onPointerUp);
      this.addEventListener('contextmenu', this._onContextMenu);
      window.addEventListener('dialog-close', this._reset);
    }

    disconnectedCallback() {
      this.removeEventListener('wheel', this._onWheel);
      this.removeEventListener('dblclick', this._onDblClick);
      this.removeEventListener('pointerdown', this._onPointerDown);
      this.removeEventListener('pointermove', this._onPointerMove);
      this.removeEventListener('pointerup', this._onPointerUp);
      this.removeEventListener('pointercancel', this._onPointerUp);
      this.removeEventListener('contextmenu', this._onContextMenu);
      window.removeEventListener('dialog-close', this._reset);
    }

    _onWheel(e) {
      e.preventDefault();
      const direction = -Math.sign(e.deltaY);
      const factor = e.deltaMode === 0 ? Math.min(0.5, Math.abs(e.deltaY) / 200) : this.zoomStep;
      this._zoomAt(this.scale + direction * factor, e.clientX, e.clientY);
    }

    _onDblClick(e) {
      e.preventDefault();
      const next = this.scale > 1.05 ? 1 : 2.5;
      this._zoomAt(next, e.clientX, e.clientY);
    }

    _onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { this.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }

      if (this._pointers.size === 2) {
        const pts = Array.from(this._pointers.values());
        this._pinchStartDistance = this._distance(pts[0], pts[1]);
        this._pinchStartScale = this.scale;
        this._dragging = false;
        this.classList.remove('is-dragging');
        return;
      }

      if (this.scale > 1) {
        this._dragging = true;
        this._dragStart = { x: e.clientX, y: e.clientY };
        this._translateStart = { x: this.translateX, y: this.translateY };
        this.classList.add('is-dragging');
      }
    }

    _onPointerMove(e) {
      if (!this._pointers.has(e.pointerId)) return;
      this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this._pointers.size === 2 && this._pinchStartDistance) {
        const pts = Array.from(this._pointers.values());
        const distance = this._distance(pts[0], pts[1]);
        const ratio = distance / this._pinchStartDistance;
        const center = {
          x: (pts[0].x + pts[1].x) / 2,
          y: (pts[0].y + pts[1].y) / 2,
        };
        this._zoomAt(this._pinchStartScale * ratio, center.x, center.y, true);
        return;
      }

      if (!this._dragging) return;

      const dx = e.clientX - this._dragStart.x;
      const dy = e.clientY - this._dragStart.y;
      this.translateX = this._translateStart.x + dx;
      this.translateY = this._translateStart.y + dy;
      this._clamp();
      this._apply(true);
    }

    _onPointerUp(e) {
      this._pointers.delete(e.pointerId);
      try { this.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }

      if (this._pointers.size < 2) {
        this._pinchStartDistance = null;
      }
      if (this._pointers.size === 0) {
        this._dragging = false;
        this.classList.remove('is-dragging');
      }
    }

    _onContextMenu(e) {
      if (this.scale > 1) e.preventDefault();
    }

    _zoomAt(targetScale, clientX, clientY, instant = false) {
      const next = Math.max(this.minScale, Math.min(this.maxScale, targetScale));
      if (Math.abs(next - this.scale) < 0.001) return;

      const rect = this.getBoundingClientRect();
      const cx = clientX - rect.left - rect.width / 2;
      const cy = clientY - rect.top - rect.height / 2;

      // Solve for translate so that the screen point (cx, cy) stays put across the
      // scale change. Derivation: with transform = translate(t) scale(s) around the
      // element center, a point at offset p in the untransformed image ends up at
      // screen offset t + s * p. Holding that offset constant when s changes:
      //   t_new = c * (1 - s_new/s_old) + (s_new/s_old) * t_old
      const factor = next / this.scale;
      this.translateX = cx * (1 - factor) + factor * this.translateX;
      this.translateY = cy * (1 - factor) + factor * this.translateY;
      this.scale = next;

      this._clamp();
      this._apply(instant);
      this.classList.toggle('is-zoomed', this.scale > 1.001);
    }

    _clamp() {
      if (this.scale <= 1) {
        this.translateX = 0;
        this.translateY = 0;
        return;
      }
      const rect = this.getBoundingClientRect();
      const maxX = ((this.scale - 1) * rect.width) / 2;
      const maxY = ((this.scale - 1) * rect.height) / 2;
      this.translateX = Math.max(-maxX, Math.min(maxX, this.translateX));
      this.translateY = Math.max(-maxY, Math.min(maxY, this.translateY));
    }

    _apply(instant = false) {
      const img = this.image;
      if (!img) return;
      if (instant) {
        img.style.transition = 'none';
        requestAnimationFrame(() => { img.style.transition = ''; });
      }
      img.style.setProperty('--drag-zoom-scale', this.scale);
      img.style.setProperty('--drag-zoom-translate-x', `${this.translateX}px`);
      img.style.setProperty('--drag-zoom-translate-y', `${this.translateY}px`);
    }

    _reset() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this._pointers.clear();
      this._pinchStartDistance = null;
      this._dragging = false;
      this.classList.remove('is-dragging', 'is-zoomed');
      this._apply();
    }

    _distance(a, b) {
      return Math.hypot(b.x - a.x, b.y - a.y);
    }
  }

  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}
